import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCampus } from '../context/CampusContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Shield, Database, Leaf, Wine, Trophy, Heart,
  LogOut, FileText, Plus, History, Sparkles, ArrowRight, Settings,
  Building, Bot, GraduationCap, Globe, BookOpen, User, Users as UsersIcon,
  Briefcase, Code, BarChart, Monitor, Key, Zap, Loader2
} from 'lucide-react';

const iconMap = {
  Shield: Shield,
  Database: Database,
  Leaf: Leaf,
  Wine: Wine,
  Trophy: Trophy,
  Heart: Heart,
  Building: Building,
  Bot: Bot,
  GraduationCap: GraduationCap,
  Globe: Globe,
  BookOpen: BookOpen,
  User: User,
  Users: UsersIcon,
  Briefcase: Briefcase,
  Code: Code,
  BarChart: BarChart,
  Monitor: Monitor,
  Key: Key,
  Zap: Zap
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { campuses, setSelectedCampus, loading } = useCampus();
  const navigate = useNavigate();

  const handleCampusSelect = (campus) => {
    setSelectedCampus(campus);
    navigate('/create');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">ContentIA Creator</h1>
              <p className="text-xs text-slate-500">Creación de contenidos</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/history')}
              data-testid="history-btn"
              className="text-slate-600"
            >
              <History className="w-4 h-4 mr-2" />
              Historial
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                data-testid="admin-btn"
                className="text-slate-600"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
              <span className="text-sm text-slate-600">Hola, {user?.name}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-slate-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            Selecciona tu Campus
          </h2>
          <p className="text-slate-500 text-lg">
            Elige el campus para el que quieres crear contenido
          </p>
        </div>

        {/* Campus Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="campus-grid">
          {loading ? (
            // Loading Skeletons
            [1, 2, 3].map((n) => (
              <Card key={n} className="border-2 border-slate-100 opacity-60">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-200 animate-pulse mb-3" />
                  <div className="h-6 w-3/4 bg-slate-200 animate-pulse rounded mb-2" />
                  <div className="h-4 w-1/2 bg-slate-200 animate-pulse rounded" />
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-full bg-slate-100 animate-pulse rounded" />
                </CardContent>
              </Card>
            ))
          ) : campuses.length > 0 ? (
            campuses.map((campus) => {
              const IconComponent = iconMap[campus.icon] || Building;
              return (
                <Card
                  key={campus.id}
                  className={`content-card cursor-pointer border-2 hover:border-indigo-300 ${campus.cssClass || ''}`}
                  onClick={() => handleCampusSelect(campus)}
                  data-testid={`campus-card-${campus.id}`}
                >
                  <CardHeader className="pb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                      style={{ backgroundColor: campus.color + '20', color: campus.color }}
                    >
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-lg text-slate-900">{campus.name}</CardTitle>
                    <CardDescription className="text-slate-500">
                      {campus.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button
                      variant="ghost"
                      className="w-full justify-between text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 p-0"
                      data-testid={`campus-select-${campus.id}`}
                    >
                      <span className="flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Crear contenido
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full py-12 text-center bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No hay campus disponibles</h3>
              <p className="text-slate-500 mb-6">Contacta con un administrador para empezar</p>
              {user?.role === 'admin' && (
                <Button onClick={() => navigate('/admin')}>
                  Ir al panel de Admin
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-6 flex items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Artículos creados</h3>
                <p className="text-sm text-slate-500">
                  Accede a todos tus artículos generados
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/history')}
                data-testid="view-articles-btn"
              >
                Ver todos
              </Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white">
            <CardContent className="p-6 flex items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Plus className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Nuevo artículo</h3>
                <p className="text-sm text-slate-500">
                  Comienza a crear contenido nuevo
                </p>
              </div>
              <Button
                onClick={() => navigate('/create')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="new-article-btn"
              >
                Crear
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
